import { useState } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import type { IntakeSubmission } from '@/api/intake/intakeApi.types';
import type { Patient } from '@/api/patients/patientsApi.types';
import type { PreVisitPhoto } from '@/api/preVisit/preVisitApi.types';
import { buildPreVisitQuestionsByCode } from '@/api/preVisit/preVisitApi.types';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Icon, type IconName } from '@/components/common/Icon';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { PreVisitAnswersList } from '@/components/common/PreVisitAnswersList';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { formatFileSize } from '@/utils/fileSize';
import { useWorkspacePatientOptionsQuery } from '@/hooks/useWorkspacePatientOptions';
import { useWorkspacePreVisitQuestionsQuery } from '@/hooks/usePreVisitQuestions';
import { buildOwnerReportedHealth, buildProposedPetChanges } from './proposedPetChanges';
import { PetChangesList } from './PetChangesList';

interface IntakeSubmissionDetailsProps {
  submission: IntakeSubmission;
  patient: Patient | null;
}

export function IntakeSubmissionDetails({ submission, patient }: IntakeSubmissionDetailsProps) {
  const { t } = useTranslation(['intake', 'patients', 'files', 'common']);
  const optionsQuery = useWorkspacePatientOptionsQuery();
  const speciesOptions = optionsQuery.data === undefined ? [] : optionsQuery.data.species;
  const proposedChanges = buildProposedPetChanges(submission, patient, t, speciesOptions);
  const ownerReportedHealth = buildOwnerReportedHealth(submission, t);
  const preVisitQuestionsQuery = useWorkspacePreVisitQuestionsQuery();
  const preVisitQuestionsByCode = buildPreVisitQuestionsByCode(
    preVisitQuestionsQuery.data === undefined ? [] : preVisitQuestionsQuery.data
  );
  const [previewedPhoto, setPreviewedPhoto] = useState<PreVisitPhoto | null>(null);

  return (
    <div className="space-y-5">
      {submission.submittedAt === null ? null : (
        <p className="text-[11px] text-zoomuted">
          {t('intake:reviewSection.submittedOn', {
            date: formatIsoDateTimeToTimestamp(submission.submittedAt),
          })}
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        <div className="space-y-5 min-w-0">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-1.5">
              {t('intake:reviewSection.issueLabel')}
            </p>
            <p className="text-sm text-zooindigo whitespace-pre-wrap">
              {submission.issueDescription === null ||
              submission.issueDescription.trim().length === 0
                ? t('intake:review.noIssue')
                : submission.issueDescription}
            </p>
          </div>

          {ownerReportedHealth.length === 0 ? null : (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-1.5">
                {t('intake:reviewSection.ownerHealthTitle')}
              </p>
              <dl className="space-y-1.5">
                {ownerReportedHealth.map(function renderHealthRow(row) {
                  return (
                    <div key={row.label}>
                      <dt className="text-[11px] font-semibold text-zoomuted">{row.label}</dt>
                      <dd className="text-sm text-zooindigo whitespace-pre-wrap">{row.value}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}

          <PetChangesList changes={proposedChanges} isNewPet={patient === null} />

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-1.5">
              {t('intake:reviewSection.filesTitle')}
            </p>
            {submission.files.length === 0 ? (
              <p className="text-sm text-zoomuted">{t('intake:reviewSection.noFiles')}</p>
            ) : (
              <div className="space-y-2">
                {submission.files.map(function renderFile(file) {
                  return (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-2.5 border border-zooborder rounded-xl"
                    >
                      <span className="w-9 h-9 rounded-lg bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
                        <Icon name={resolveFileIcon(file.contentType)} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-zooindigo truncate">
                          {file.filename}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge tone="muted" size="sm">
                            {formatFileSize(file.sizeBytes)}
                          </Badge>
                          <Badge tone={resolveFileStatusTone(file.status)} size="sm">
                            {t('files:status.' + file.status)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-5 min-w-0">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-1.5">
              {t('intake:reviewSection.preVisitAnswersTitle')}
            </p>
            <PreVisitAnswersList
              answers={submission.preVisitAnswers}
              questionsByCode={preVisitQuestionsByCode}
              onPreviewPhoto={setPreviewedPhoto}
            />
          </div>

          {submission.answers.length === 0 ? null : (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-1.5">
                {t('intake:reviewSection.answersTitle')}
              </p>
              <dl className="space-y-1.5">
                {submission.answers.map(function renderAnswer(answer) {
                  return (
                    <div key={answer.fieldCode}>
                      <dt className="text-[11px] font-semibold text-zoomuted">
                        {localizedAnswerLabel(answer.labelEn, answer.labelPl)}
                      </dt>
                      <dd className="text-sm text-zooindigo">
                        {answer.value === null || answer.value.trim().length === 0
                          ? '—'
                          : answer.value}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}
        </div>
      </div>

      {submission.reviewNote === null ? null : (
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-1.5">
            {t('intake:reviewSection.reviewNoteLabel')}
          </p>
          <p className="text-sm text-zooindigo">{submission.reviewNote}</p>
        </div>
      )}

      <FilePreviewModal
        isOpen={previewedPhoto !== null}
        onClose={function closePreview() {
          setPreviewedPhoto(null);
        }}
        url={previewedPhoto === null ? '' : previewedPhoto.url}
        filename={previewedPhoto === null ? '' : previewedPhoto.filename}
        contentType={previewedPhoto === null ? null : previewedPhoto.contentType}
      />
    </div>
  );
}

function resolveFileIcon(contentType: string): IconName {
  if (contentType.startsWith('image/')) {
    return 'Image';
  }
  if (contentType.startsWith('video/')) {
    return 'Video';
  }
  return 'FileText';
}

function resolveFileStatusTone(status: string): BadgeTone {
  if (status === 'approved') {
    return 'success';
  }
  if (status === 'rejected') {
    return 'danger';
  }
  return 'neutral';
}

function localizedAnswerLabel(labelEn: string, labelPl: string): string {
  if (i18next.language === 'pl' && labelPl.length > 0) {
    return labelPl;
  }
  return labelEn;
}
